import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {of, Subject, Subscription} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {RateLimitOverride, RouteGroup} from 'src/app/models';
import {IndividualsService} from 'src/app/services';
import {isEmail} from 'src/app/utilities/email';

const ONE_OR_MORE = RegExp('^[1-9][0-9]*$');
// API rejects expires_at on or after 2038-01-19T00:00Z (DB TIMESTAMP range). The day is saved as
// 23:59:59 local, so stop a day earlier to stay below the limit in every time zone.
const MAX_EXPIRES_ON = new Date(2038, 0, 17);

@Component({
    selector: 'app-rate-limit-override-popup',
    templateUrl: './rate-limit-override-popup.component.html',
    styleUrls: ['./rate-limit-override-popup.component.scss']
})
export class RateLimitOverridePopupComponent implements OnInit, OnDestroy {
    routeGroups: {value: RouteGroup, label: string}[] = [
        {value: 'message', label: 'Message'},
        {value: 'alertManager', label: 'Alert Manager'},
        {value: 'pagerduty', label: 'Pager Duty'}
    ];
    override: RateLimitOverride;
    expiresOn: Date = null;
    maxExpiresOn = MAX_EXPIRES_ON;
    isEdit: boolean;
    callerOptions: { name: string, email: string }[] = [];
    private callerTerms = new Subject<string>();
    private callerLookup: Subscription;

    constructor(
        public dialogRef: MatDialogRef<RateLimitOverridePopupComponent>,
        private individualsService: IndividualsService,
        @Inject(MAT_DIALOG_DATA) public data: RateLimitOverride
    ) {
    }

    ngOnInit(): void {
        this.initOverride();
        // Same lookup as the Message Status "From" search: service accounts are individuals ("[svc]").
        this.callerLookup = this.callerTerms.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(term => !term ? of([]) : this.individualsService.getIndividuals(term).pipe(
                map(people => (people || []).filter((p: any) => p.associate_email)
                    .map((p: any) => ({name: p.full_name || `${p.first_name} ${p.last_name}`, email: p.associate_email}))),
                catchError(() => of([]))))
        ).subscribe(options => this.callerOptions = options);
    }

    private initOverride(): void {
        const data = this.data;
        this.isEdit = !!data?.id;
        this.override = {
            id: data?.id,
            caller: data?.caller ?? '',
            route_group: data?.route_group ?? 'alertManager',
            replenish_rate: data?.replenish_rate,
            burst_capacity: data?.burst_capacity,
            alert_cap: data?.alert_cap,
            justification: data?.justification ?? '',
            ticket: data?.ticket ?? '',
            enabled: data?.enabled ?? true,
            version: data?.version
        };
        this.expiresOn = data?.expires_at ? new Date(data.expires_at) : null;
    }

    ngOnDestroy(): void {
        this.callerLookup?.unsubscribe();
    }

    onCallerChanged(value: string): void {
        const term = value?.trim() || '';
        this.callerTerms.next(term.length > 1 && !isEmail(term) ? term : '');
    }

    // Limits are whole numbers: block '.', 'e', '+', '-' that a number input otherwise accepts.
    blockNonDigits(event: KeyboardEvent): void {
        if (event.key.length === 1 && !/[0-9]/.test(event.key) && !event.ctrlKey && !event.metaKey) {
            event.preventDefault();
        }
    }

    blockNonDigitPaste(event: ClipboardEvent): void {
        if (!/^[0-9]+$/.test(event.clipboardData?.getData('text')?.trim() ?? '')) {
            event.preventDefault();
        }
    }

    isAlertManager(): boolean {
        return this.override.route_group === 'alertManager';
    }

    getValidationError(): string {
        const o = this.override;
        if (!o.caller?.trim()) {
            return 'Caller is required.';
        }
        const limits = [o.replenish_rate, o.burst_capacity, this.isAlertManager() ? o.alert_cap : null]
            .filter(v => v !== null && v !== undefined && `${v}` !== '');
        if (limits.length === 0) {
            return 'Set at least one limit.';
        }
        if (limits.some(v => !ONE_OR_MORE.test(`${v}`))) {
            return 'Limits must be whole numbers of 1 or more.';
        }
        if (!o.justification?.trim()) {
            return 'Justification is required.';
        }
        if (!o.ticket?.trim()) {
            return 'Ticket is required.';
        }
        return null;
    }

    save(): void {
        if (!this.getValidationError()) {
            this.dialogRef.close(this.result());
        }
    }

    private result(): RateLimitOverride {
        const toNumber = (v: any) => (v === null || v === undefined || `${v}` === '') ? null : Number(v);
        // Expires at the end of the selected day (local time).
        const expiresAt = this.expiresOn
            ? new Date(this.expiresOn.getFullYear(), this.expiresOn.getMonth(), this.expiresOn.getDate(), 23, 59, 59).getTime()
            : null;
        return {
            ...this.override,
            caller: this.override.caller.trim(),
            replenish_rate: toNumber(this.override.replenish_rate),
            burst_capacity: toNumber(this.override.burst_capacity),
            alert_cap: this.isAlertManager() ? toNumber(this.override.alert_cap) : null,
            justification: this.override.justification.trim(),
            ticket: this.override.ticket.trim(),
            expires_at: expiresAt
        };
    }
}
